import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "./CountryName";
import {
  Briefcase,
  MapPin,
  Calendar,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Building2,
  Clock,
  Globe,
  UserCircle2,
  TrendingUp
} from "lucide-react";

const getEmptyWork = () => ({
  companyName: "",
  job: "",
  position: "",
  city: "",
  country: "",
  companyAddress: "",
  from: "",
  to: "",
});

const WorkForm = () => {
  const [workList, setWorkList] = useState([getEmptyWork()]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  console.log('preview', preview);

  const formatDateForInput = (date) => (date ? new Date(date).toISOString().split("T")[0] : "");

  useEffect(() => {
    const fetchWorkHistory = async () => {
      setLoading(true);
      try {
        const res = await makeRequestAuth("get", "/user/getWorkHistory");
        if (res?.data?.work) {
          const formatted = res.data.work.map((w) => ({
            ...w,
            from: formatDateForInput(w.from),
            to: formatDateForInput(w.to),
          }));

          setWorkList(formatted.length ? formatted : [getEmptyWork()]);

          setPreview({
            ...res.data,
            work: formatted,
          });
        }
      } catch (err) {
        console.error("Fresh history mode.");
      } finally {
        setLoading(false);
      }
    };
    fetchWorkHistory();
  }, []);

  // useEffect(() => {
  //   const fetchWorkHistory = async () => {
  //     setLoading(true);
  //     try {
  //       const res = await makeRequestAuth("get", "/user/getWorkHistory");
  //       if (res?.data?.work) {
  //         const formatted = res.data.work.map((w) => ({
  //           ...w,
  //           from: formatDateForInput(w.from),
  //           to: formatDateForInput(w.to),
  //         }));

  //         setWorkList(formatted.length ? formatted : [getEmptyWork()]);

  //         setPreview({
  //           ...res.data,
  //           work: formatted,
  //         });
  //       }
  //     } catch (err) {
  //       console.error("Fresh history mode.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   fetchWorkHistory();
  // }, [preview]);

  const handleChange = (index, field, value) => {
    const updated = [...workList];
    updated[index][field] = value;
    if (field === "from" && updated[index].to && value > updated[index].to) {
      updated[index].to = "";
    }
    setWorkList(updated);
  };

  const validateForm = () => {
    let list = [];
    workList.forEach((w, i) => {
      const row = `Exp #${i + 1}`;
      if (!w.companyName?.trim()) list.push(`${row}: Company Name is required`);
      if (!w.job?.trim()) list.push(`${row}: Field/Occupation is required`);
      if (!w.position?.trim()) list.push(`${row}: Position is required`);
      if (!w.country) list.push(`${row}: Country is missing`);
      if (!w.from) list.push(`${row}: Join date is missing`);
      if (!w.to) list.push(`${row}: Left date is missing`);
    });
    return list;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        work: workList.map((w) => ({
          companyName: w.companyName,
          job: w.job,
          position: w.position,
          city: w.city,
          country: w.country,
          companyAddress: w.companyAddress,
          from: w.from,
          to: w.to,
        })),
      };

      const res = await makeRequestAuth(
        "post",
        "/user/createOrUpdateWorkHistory",
        payload
      );

      if (res?.data?.work) {
        const formatted = res.data.work.map((w) => ({
          ...w,
          from: formatDateForInput(w.from),
          to: formatDateForInput(w.to),
        }));

        setWorkList(formatted.length ? formatted : [getEmptyWork()]);

        setPreview({
          ...res.data,
          work: formatted,
        });
      }
      // depends on your response structure
      showSuccessToast("Professional history updated! ✅");
    } catch (err) {
      console.error(err);
      showErrorToast("Update failed. Please check details.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const STATUS_CONFIG = {
    pending: { bg: "bg-amber-100", text: "text-amber-800", icon: <Clock size={14} /> },
    approve: { bg: "bg-emerald-100", text: "text-emerald-800", icon: <CheckCircle2 size={14} /> },
    reject: { bg: "bg-rose-100", text: "text-rose-800", icon: <AlertCircle size={14} /> }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-8 font-sans antialiased text-slate-700">
      <ToastContainer />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-6 border-b-2 border-slate-50">
        <div className="flex items-center gap-5">
          <div className="p-4 bg-[#132b45] text-white rounded-2xl shadow-xl shadow-blue-900/10">
            <Briefcase size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#132b45] tracking-tight">Employment History</h1>
            <p className="text-slate-500 font-medium uppercase text-[10px] tracking-[0.2em] mt-1">Official Career Declaration</p>
          </div>
        </div>
        {preview && (
          <div className={`flex items-center gap-2 px-6 py-2.5 rounded-full border font-bold uppercase text-[10px] tracking-widest ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.bg || 'bg-slate-100'} ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.text || 'text-slate-600'}`}>
            {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon || <Clock size={14} />}
            {preview?.status || "Processing"}
          </div>
        )}
      </div>

      {/* ERROR SUMMARY */}
      {errors.length > 0 && (
        <div className="bg-rose-50 border-2 border-rose-100 p-6 rounded-3xl mb-10">
          <p className="text-rose-800 font-bold text-xs uppercase mb-3 flex items-center gap-2">
            <AlertCircle size={18} /> Review Validation:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] font-medium text-rose-600">
            {errors.map((err, i) => <li key={i}>• {err}</li>)}
          </ul>
        </div>
      )}

      {preview?.work ? (
        /* ================= PREVIEW MODE ================= */
        <div className="space-y-8 animate-in fade-in duration-500">
          <div className="grid grid-cols-1 gap-8">
            {preview.work.map((w, i) => (
              <div key={w._id || i} className="relative flex flex-col lg:flex-row gap-6 lg:pl-16 group">
                <div className="absolute left-0 top-0 hidden lg:flex h-12 w-12 rounded-2xl bg-white border-2 border-slate-100 text-slate-300 items-center justify-center z-10 shadow-sm group-hover:border-[#132b45] group-hover:text-[#132b45] transition-all">
                  <TrendingUp size={20} />
                </div>

                <div className="flex-1 bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm group-hover:shadow-md transition-all">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 pb-6 border-b border-slate-50">
                    <div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-[9px] font-bold uppercase tracking-wider">
                          {w.position || "Staff"}
                        </span>
                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
                          <Globe size={10} /> {w.country}
                        </span>
                      </div>
                      <h3 className="font-bold text-[#132b45] text-2xl uppercase tracking-tight">
                        {w.companyName}
                      </h3>
                    </div>

                    <div className="flex items-center gap-3 bg-slate-50 px-5 py-3 rounded-2xl border border-slate-100">
                      <div className="text-center">
                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">Joined</p>
                        <p className="text-xs font-bold text-slate-700">{formatDate(w.from)}</p>
                      </div>
                      <ChevronRight size={16} className="text-slate-300" />
                      <div className="text-center">
                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">Left</p>
                        <p className="text-xs font-bold text-slate-700">{formatDate(w.to)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <PreviewItem icon={<UserCircle2 size={18} className="text-blue-500" />} label="Field / Job" value={w.job} />
                    <PreviewItem icon={<Building2 size={18} className="text-emerald-500" />} label="City" value={w.city} />
                    <PreviewItem icon={<MapPin size={18} className="text-rose-500" />} label="Address" value={w.companyAddress} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {preview.status === 'reject' && (
            <div className="flex justify-center pt-4">
              <button onClick={() => setPreview(null)} className="bg-[#132b45] text-white px-10 py-4 rounded-2xl font-bold text-[10px] uppercase tracking-[0.2em] hover:bg-black transition-all shadow-xl">
                Edit
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ================= FORM MODE ================= */
        <form onSubmit={handleSubmit} className="space-y-10 animate-in slide-in-from-bottom-4 duration-500">
          {workList.map((work, index) => (
            <div key={index} className="bg-white border-2 border-slate-50 rounded-[3rem] p-8 md:p-10 relative shadow-sm">
              <div className="flex justify-between items-center mb-10">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 bg-[#132b45] text-white flex items-center justify-center rounded-2xl font-bold shadow-lg shadow-blue-900/20">{index + 1}</span>
                  <h2 className="font-bold text-[#132b45] uppercase tracking-[0.15em] text-[11px]">Experience Details</h2>
                </div>
                {workList.length > 1 && (
                  <button type="button" onClick={() => setWorkList(workList.filter((_, i) => i !== index))} className="text-rose-400 hover:text-rose-600 p-2 hover:bg-rose-50 rounded-xl transition-all">
                    <Trash2 size={20} />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <InputField label="Company Name *" icon={<Building2 size={16} />} placeholder="e.g. Google" value={work.companyName} onChange={(v) => handleChange(index, "companyName", v)} />
                <InputField label="Field/Job *" icon={<Briefcase size={16} />} placeholder="e.g. Technology" value={work.job} onChange={(v) => handleChange(index, "job", v)} />
                <InputField label="Position *" icon={<UserCircle2 size={16} />} placeholder="e.g. Senior Lead" value={work.position} onChange={(v) => handleChange(index, "position", v)} />
                <InputField label="City *" icon={<MapPin size={16} />} placeholder="e.g. Mumbai" value={work.city} onChange={(v) => handleChange(index, "city", v)} />

                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Globe size={14} /> Country *
                  </label>
                  <select
                    value={work.country}
                    onChange={(e) => handleChange(index, "country", e.target.value)}
                    className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-4 font-medium text-slate-700 outline-none focus:border-[#132b45] focus:bg-white transition-all appearance-none cursor-pointer text-sm"
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <InputField label="Office Address" icon={<MapPin size={16} />} placeholder="Full street details" value={work.companyAddress} onChange={(v) => handleChange(index, "companyAddress", v)} />
                </div>

                <InputField type="date" label="Join Date *" icon={<Calendar size={16} />} max={new Date().toISOString().split("T")[0]} value={work.from} onChange={(v) => handleChange(index, "from", v)} />
                <InputField type="date" label="End Date *" icon={<Calendar size={16} />} min={work.from} value={work.to} onChange={(v) => handleChange(index, "to", v)} />
              </div>
            </div>
          ))}

          <div className="flex flex-col md:flex-row items-center gap-6 pt-4">
            <button
              type="button"
              onClick={() => setWorkList([...workList, getEmptyWork()])}
              className="w-full md:w-auto px-10 py-5 border-2 border-dashed border-slate-200 rounded-3xl text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:border-[#132b45] hover:text-[#132b45] transition-all bg-white"
            >
              + Add Another Experience
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full md:flex-1 bg-[#132b45] text-white py-5 rounded-3xl font-bold text-xs uppercase tracking-[0.2em] shadow-2xl shadow-blue-900/20 hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save Professional Records"} <ChevronRight size={18} />
            </button>
          </div>
        </form>
      )}

      {/* LOADER */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-[2px] z-50 flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
          <p className="font-bold text-[10px] uppercase tracking-[0.3em] text-white">Syncing Data</p>
        </div>
      )}
    </div>
  );
};

const InputField = ({ label, value, onChange, type = "text", placeholder, icon, min, max }) => (
  <div className="flex flex-col gap-2">
    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
      {icon} {label}
    </label>
    <input
      type={type}
      value={value || ""}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-6 py-4 font-medium text-[#132b45] placeholder:text-slate-300 outline-none focus:border-[#132b45] focus:bg-white transition-all text-sm"
    />
  </div>
);

const PreviewItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="mt-1 p-2 bg-slate-50 rounded-xl">{icon}</div>
    <div className="flex flex-col">
      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</span>
      <span className="font-bold text-[#132b45] text-sm leading-tight">{value || "Not Specified"}</span>
    </div>
  </div>
);

export default WorkForm;